import styled from '@emotion/styled';
import { Prismic } from '@landsbankinn/prismic';
import { LayoutContext } from '@landsbankinn/slices/components/contexts/LayoutContext';
import React, { useState, useEffect, useContext } from 'react';
import { a, useSpring } from 'react-spring';
import { MobileNav } from 'components/mobile-nav/MobileNav';
import { NavBar } from './NavBar';
import { Sheet } from './Sheet';

export interface LinkMeta {
  id?: string;
  lang?: string;
  type?: string;
  uid?: string;
}

export interface NavItemLevel3 {
  third_level_link?: Prismic.Link;
  third_level_link_text: string;
}

export interface NavItemLevel2 {
  primary: {
    link_text: string;
    nav_link?: Prismic.Link;
  };
  fields?: NavItemLevel3[];
}

export interface NavItem {
  navigation_title: Prismic.RichText;
  navigation_item: {
    _meta?: LinkMeta;
    body?: NavItemLevel2[];
    title: Prismic.RichText;
    link?: Prismic.Link;
    visible?: boolean;
  };
}

interface NavigationProps {
  title: Prismic.RichText;
  lang: string;
  navigation: NavItem[];
  quicklinks: any;
  openingHours: Prismic.RichText;
  bottomLinks: any;
  activeIds: string[];
  cards: any;
  isTransparent: boolean;
  page?: { title?: string };
}

const Host = styled.div`
  height: 80px;
  @media (max-width: 949px) {
    display: none;
  }
`;

const Sticky = styled(a.div)`
  position: relative;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  z-index: 100;
`;

export const Navigation = (props: NavigationProps) => {
  const { isSubnav, isNavigationOpen, setIsNavigationOpen } =
    useContext(LayoutContext);
  const {
    navigation,
    quicklinks,
    openingHours,
    bottomLinks,
    cards,
    activeIds,
    page,
    isTransparent,
    lang,
  } = props;
  const [active, setActive] = useState<NavItem | null>(null);

  const onNavBarItemClick = (item: NavItem) => {
    if (item === active) {
      setIsNavigationOpen((x) => !x);
    } else {
      if (active) {
        setActive(null);
      }
      requestAnimationFrame(() => setActive(item));
    }
  };

  useEffect(() => {
    active && setIsNavigationOpen(true);
  }, [active]);

  const [sticky, setSticky] = useState(0);
  const { y } = useSpring({
    y: sticky === 2 ? 0 : -80,
  });

  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.keyCode === 27) {
        setIsNavigationOpen(false);
      }
    };
    const htmlEl = document.querySelector('html');
    const heroEl = document.querySelector<HTMLDivElement>('#hero');
    const boundingRect = heroEl?.getBoundingClientRect();
    const onScroll = () => {
      if (htmlEl?.classList?.contains('is-scroll-disabled') || !boundingRect) {
        // no-op on menu open
        return;
      }

      if (window.scrollY <= boundingRect.top) {
        setSticky(0);
      } else if (window.scrollY > boundingRect.top + boundingRect.height) {
        setSticky(2);
      } else {
        setSticky(1);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('keyup', onKeyUp, false);

    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('keyup', onKeyUp, false);
    };
  }, []);

  return (
    <>
      <MobileNav
        open={isNavigationOpen}
        items={navigation}
        activeIds={activeIds}
        pageTitle={page?.title}
        bottomLinks={bottomLinks}
      />
      <Host>
        <Sticky
          style={
            isSubnav
              ? {}
              : {
                  position:
                    sticky === 0 && !isNavigationOpen ? 'relative' : 'fixed',
                  y: sticky !== 0 && !isNavigationOpen ? y : undefined,
                }
          }
        >
          <NavBar
            title={Prismic.text(props?.title) ?? undefined}
            active={isNavigationOpen ? active : null}
            items={navigation}
            onClick={onNavBarItemClick}
            activeIds={activeIds}
            isTransparent={isTransparent}
            sticky={!!sticky}
            lang={lang}
          />
          <Sheet
            open={isNavigationOpen}
            item={active ?? undefined}
            openingHours={openingHours}
            bottomLinks={bottomLinks}
            quickLinks={quicklinks}
            activeIds={activeIds}
            cards={cards}
          />
        </Sticky>
      </Host>
    </>
  );
};
